import {type BufferGeometry, EdgesGeometry, LineBasicMaterial, LineSegments, Mesh, MeshBasicMaterial,} from 'three';
import type {ViewportGizmoControlAction} from './ViewportGizmoControlAction';


/** 视口方向导航器外部控制按钮网格。 */
export class ViewportGizmoOuterControlMesh extends Mesh<
  BufferGeometry,
  MeshBasicMaterial
> {
  /** 当前按钮触发的有限相机动作。 */
  public readonly action: ViewportGizmoControlAction;

  /** 按钮默认颜色。 */
  private readonly defaultColor = '#a8a8a8';

  /** 按钮悬停颜色。 */
  private readonly hoverColor = '#93d3eb';

  /** 按钮可见轮廓。 */
  private readonly outline: LineSegments<EdgesGeometry, LineBasicMaterial>;

  /**
   * 创建外部视图控制按钮。
   *
   * @param geometry 按钮显示和拾取共用几何。
   * @param action 点击后触发的相机动作。
   */
  public constructor(
    geometry: BufferGeometry,
    action: ViewportGizmoControlAction,
  ) {
    const material = new MeshBasicMaterial({
      color: '#a8a8a8',
      transparent: true,
      opacity: 0.08,
      depthTest: false,
      depthWrite: false,
    });
    super(geometry, material);

    this.action = action;
    this.name = `视口方向外部控件-${action}`;
    this.renderOrder = 80;

    // 半透明面扩大拾取面积，独立轮廓负责呈现空心按钮外观。
    this.outline = new LineSegments(
      new EdgesGeometry(geometry),
      new LineBasicMaterial({
        color: this.defaultColor,
        transparent: true,
        opacity: 0.9,
        depthTest: false,
        depthWrite: false,
      }),
    );
    this.outline.raycast = () => undefined;
    this.outline.renderOrder = this.renderOrder + 1;
    this.add(this.outline);
  }

  /**
   * 设置按钮悬停视觉状态。
   *
   * @param hovered 是否命中当前按钮。
   */
  public setHovered(hovered: boolean): void {
    // 面与轮廓必须同步变化，避免只有拾取面或只有边线获得反馈。
    this.material.color.set(hovered ? this.hoverColor : this.defaultColor);
    this.material.opacity = hovered ? 0.32 : 0.08;
    this.outline.material.color.set(
      hovered ? this.hoverColor : this.defaultColor,
    );
    this.outline.material.opacity = hovered ? 1 : 0.9;
  }

  /** 释放按钮几何和材质资源。 */
  public dispose(): void {
    // 创建处拥有全部几何和材质，因此在同一对象内完成对称释放。
    this.geometry.dispose();
    this.material.dispose();
    this.outline.geometry.dispose();
    this.outline.material.dispose();
    this.clear();
  }
}

/**
 * 判断未知对象是否为视口方向导航器外部按钮。
 *
 * @param value 待检查对象。
 * @returns 对象继承专用网格时返回 `true`。
 */
export function isViewportGizmoOuterControlMesh(
  value: unknown,
): value is ViewportGizmoOuterControlMesh {
  return value instanceof ViewportGizmoOuterControlMesh;
}

